import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Droplet, RefreshCw, Save, AlertCircle } from 'lucide-react';
import api from '../../config/api';

const BloodInventory = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [summary, setSummary] = useState({ totalUnits: 0 });

  // Blood type colors for visual representation
  const bloodTypeColors = {
    'A+': 'bg-red-100 border-red-300 text-red-800',
    'A-': 'bg-red-50 border-red-200 text-red-700',
    'B+': 'bg-blue-100 border-blue-300 text-blue-800',
    'B-': 'bg-blue-50 border-blue-200 text-blue-700',
    'AB+': 'bg-purple-100 border-purple-300 text-purple-800',
    'AB-': 'bg-purple-50 border-purple-200 text-purple-700',
    'O+': 'bg-green-100 border-green-300 text-green-800',
    'O-': 'bg-green-50 border-green-200 text-green-700',
  };

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      // Create default inventory if API fails
      const createDefaultInventory = () => {
        const bloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
        return bloodTypes.map(bloodType => ({
          bloodType,
          units: 0,
          lastUpdated: new Date().toISOString()
        }));
      };

      try {
        const response = await api.get('/hospitals/inventory');
        if (response.data.success) {
          // Sort by blood type for consistent display
          const sortedInventory = response.data.data.sort((a, b) => {
            const typeOrder = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
            return typeOrder.indexOf(a.bloodType) - typeOrder.indexOf(b.bloodType);
          });
          setInventory(sortedInventory);
          
          // Fetch summary data
          fetchSummary();
          return;
        }
      } catch (error) {
        console.error('Error fetching from hospital inventory endpoint:', error);
      }

      // Fallback to default inventory if API fails
      const defaultInventory = createDefaultInventory();
      setInventory(defaultInventory);
      setSummary({ totalUnits: 0, bloodTypes: defaultInventory.map(item => ({ type: item.bloodType, units: item.units })) });
    } catch (error) {
      console.error('Error in inventory process:', error);
      toast.error('Failed to load blood inventory. Using default values.');
      
      // Create default inventory as fallback
      const bloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
      const defaultInventory = bloodTypes.map(bloodType => ({
        bloodType,
        units: 0,
        lastUpdated: new Date().toISOString()
      }));
      setInventory(defaultInventory);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      // Calculate summary from inventory data
      const totalUnits = inventory.reduce((sum, item) => sum + item.units, 0);
      const bloodTypes = inventory.map(item => ({ type: item.bloodType, units: item.units }));
      setSummary({ totalUnits, bloodTypes });
    } catch (error) {
      console.error('Error calculating inventory summary:', error);
    }
  };

  const handleInputChange = (index, value) => {
    // Ensure value is a non-negative number
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 0) return;

    const updatedInventory = [...inventory];
    updatedInventory[index].units = numValue;
    setInventory(updatedInventory);
  };

  const updateInventory = async (bloodType, units) => {
    setUpdating(true);
    try {
      // Try to update using the hospital inventory endpoint
      try {
        const response = await api.put('/hospitals/inventory/update', {
          bloodType,
          units
        });
        
        if (response.data.success) {
          toast.success(`${bloodType} inventory updated successfully`);
          // Update the local state with the updated inventory item
          const updatedInventory = inventory.map(item => 
            item.bloodType === bloodType ? { ...item, units, lastUpdated: new Date().toISOString() } : item
          );
          setInventory(updatedInventory);
          
          // Refresh summary data
          fetchSummary();
          return;
        }
      } catch (apiError) {
        console.error('Error with hospital inventory API:', apiError);
      }
      
      // Fallback to local update if API fails
      const updatedInventory = inventory.map(item => 
        item.bloodType === bloodType ? { ...item, units, lastUpdated: new Date().toISOString() } : item
      );
      setInventory(updatedInventory);
      fetchSummary();
      toast.success(`${bloodType} inventory updated locally`);
    } catch (error) {
      console.error('Error updating blood inventory:', error);
      toast.error('Failed to update blood inventory');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveAll = async () => {
    setUpdating(true);
    try {
      let successfulUpdate = false;
      
      // Try to update using the hospital inventory endpoint
      try {
        const updatePromises = inventory.map(item => 
          api.put('/hospitals/inventory/update', {
            bloodType: item.bloodType,
            units: item.units
          })
        );
        
        await Promise.all(updatePromises);
        successfulUpdate = true;
      } catch (apiError) {
        console.error('Error with hospital inventory API:', apiError);
      }
      
      // Always update the local state
      const updatedInventory = inventory.map(item => ({
        ...item,
        lastUpdated: new Date().toISOString()
      }));
      setInventory(updatedInventory);
      fetchSummary();
      
      toast.success(successfulUpdate ? 
        'All blood inventory updated successfully' : 
        'Blood inventory updated locally');
      
      setEditMode(false);
    } catch (error) {
      console.error('Error updating all blood inventory:', error);
      toast.error('Failed to update blood inventory');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Blood Inventory Management</h2>
          <p className="text-gray-600 text-sm">Manage your blood bank inventory</p>
        </div>
        <div className="flex space-x-2">
          {editMode ? (
            <>
              <button
                onClick={handleSaveAll}
                disabled={updating}
                className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400"
              >
                {updating ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save All
              </button>
              <button
                onClick={() => {
                  setEditMode(false);
                  fetchInventory(); // Reset to original values
                }}
                disabled={updating}
                className="px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors disabled:bg-gray-100"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditMode(true)}
              className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Edit Inventory
            </button>
          )}
        </div>
      </div>

      {/* Summary Card */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center">
          <Droplet className="h-8 w-8 text-red-500 mr-3" />
          <div>
            <h3 className="font-semibold text-gray-800">Total Blood Units Available</h3>
            <p className="text-2xl font-bold text-red-600">{summary.totalUnits || 0} units</p>
          </div>
        </div>
      </div>

      {/* Inventory Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {inventory.map((item, index) => (
          <div 
            key={item._id || item.bloodType} 
            className={`p-4 rounded-lg border ${bloodTypeColors[item.bloodType]} transition-all ${editMode ? 'ring-2 ring-blue-300' : ''}`}
          >
            <div className="flex justify-between items-center mb-2">
              <span className="text-lg font-bold">{item.bloodType}</span>
              <Droplet className="h-5 w-5" />
            </div>
            
            {editMode ? (
              <div className="mt-2">
                <label className="block text-sm font-medium mb-1">Units Available</label>
                <input
                  type="number"
                  min="0"
                  value={item.units}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={() => updateInventory(item.bloodType, item.units)}
                  disabled={updating}
                  className="mt-2 w-full py-1 bg-white bg-opacity-50 border border-current rounded-md hover:bg-opacity-70 transition-colors text-sm"
                >
                  Update
                </button>
              </div>
            ) : (
              <div className="mt-2">
                <p className="text-3xl font-bold">{item.units}</p>
                <p className="text-sm opacity-75">units available</p>
                {item.lastUpdated && (
                  <p className="text-xs mt-2 opacity-60">
                    Updated: {new Date(item.lastUpdated).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Low Inventory Warning */}
      {inventory.some(item => item.units < 5) && (
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 text-yellow-500 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-800">Low Inventory Alert</h4>
            <p className="text-sm text-yellow-700">
              Some blood types are running low (less than 5 units). Consider updating your inventory or requesting donations.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BloodInventory;
